import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { TOURS, type TourSpec } from './tours';

const STORAGE_PREFIX = 'forvisibility.tour.';
const COMPLETED_SUFFIX = '.completed.v1';

function completedKey(tourId: string) {
  return `${STORAGE_PREFIX}${tourId}${COMPLETED_SUFFIX}`;
}

function isCompleted(tourId: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(completedKey(tourId)) === '1';
  } catch {
    return false;
  }
}

function markCompleted(tourId: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(completedKey(tourId), '1');
  } catch {
    /* ignore quota errors */
  }
}

function clearCompletion(tourId: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(completedKey(tourId));
  } catch {
    /* ignore */
  }
}

export interface TourContextValue {
  /** The tour currently being displayed, or null if none. */
  activeTour: TourSpec | null;
  /** Index of the current step within `activeTour.steps`. */
  stepIndex: number;
  /** Start a tour by id. Pass `restart: true` to clear its completion flag
   *  first so an already-finished tour will run again. */
  startTour: (id: string, opts?: { restart?: boolean }) => void;
  /** Advance, retreat, or end the active tour. */
  next: () => void;
  prev: () => void;
  end: (markDone?: boolean) => void;
  /** Whether a given tour is marked completed in localStorage. */
  isTourCompleted: (id: string) => boolean;
}

// Context object lives here for cohesion with the provider; the
// react-refresh rule wants only-component exports per file, but splitting
// this into a 4-file chain (context object / provider / hook / engine)
// hurts more than it helps for a single logical unit.
// eslint-disable-next-line react-refresh/only-export-components
export const TourContext = createContext<TourContextValue | null>(null);

interface TourProviderProps {
  children: React.ReactNode;
  /** When true, the provider auto-starts a tour the first time the user
   *  visits a path that matches `autoStartOnPath` AND that tour has not
   *  been completed. Disable this in tests or when running tour-only
   *  flows from an admin panel. */
  enableAutoStart?: boolean;
}

/** Provides the active tour state. Mount once near the root, INSIDE the
 *  Router (so `useLocation` works) but outside the AppShell so the tour
 *  engine can render its own backdrop. */
export function TourProvider({ children, enableAutoStart = true }: TourProviderProps) {
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const { pathname } = useLocation();

  const activeTour = useMemo(
    () => (activeTourId ? (TOURS.find((t) => t.id === activeTourId) ?? null) : null),
    [activeTourId],
  );

  const startTour = useCallback((id: string, opts?: { restart?: boolean }) => {
    if (opts?.restart) clearCompletion(id);
    const tour = TOURS.find((t) => t.id === id);
    if (!tour) return;
    setActiveTourId(id);
    setStepIndex(0);
  }, []);

  const end = useCallback(
    (markDone = true) => {
      if (markDone && activeTourId) markCompleted(activeTourId);
      setActiveTourId(null);
      setStepIndex(0);
    },
    [activeTourId],
  );

  const next = useCallback(() => {
    if (!activeTour) return;
    if (stepIndex >= activeTour.steps.length - 1) {
      end(true);
      return;
    }
    setStepIndex((i) => i + 1);
  }, [activeTour, stepIndex, end]);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  // Auto-start tours on first visit to their path. The check runs on every
  // pathname change but does nothing if the user has already seen the tour
  // or another tour is already active.
  useEffect(() => {
    if (!enableAutoStart) return;
    if (activeTourId) return;
    const candidate = TOURS.find((t) => t.autoStartOnPath === pathname && !isCompleted(t.id));
    if (candidate) {
      // Small delay so the page has time to mount its `data-tour-target`s.
      const handle = window.setTimeout(() => startTour(candidate.id), 250);
      return () => window.clearTimeout(handle);
    }
  }, [pathname, activeTourId, enableAutoStart, startTour]);

  const value = useMemo<TourContextValue>(
    () => ({
      activeTour,
      stepIndex,
      startTour,
      next,
      prev,
      end,
      isTourCompleted: isCompleted,
    }),
    [activeTour, stepIndex, startTour, next, prev, end],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
